import { gql } from '@apollo/client';
import { apolloClient } from '../config/apollo-client';

// GraphQL subscription for totals updates
export const TOTALS_UPDATED_SUBSCRIPTION = gql`
  subscription TotalsUpdated($parentId: Int!, $roleId: Int, $search: String, $from: String, $to: String) {
    totalsUpdated(parentId: $parentId, roleId: $roleId, search: $search, from: $from, to: $to) {
      totalBalance
      totalBalanceIn
      totalBalanceOut
      totalProfit
    }
  }
`;

// Types for totals subscription
export interface TotalsUpdatedData {
  totalBalance: number;
  totalBalanceIn: number;
  totalBalanceOut: number;
  totalProfit: number;
}

export interface TotalsSubscriptionVariables {
  parentId: number;
  roleId?: number;
  search?: string;
  from?: string;
  to?: string;
}

export interface TotalsSubscriptionCallbacks {
  onUpdate?: (data: TotalsUpdatedData) => void;
  onError?: (error: any) => void;
  onComplete?: () => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
}

// Totals subscription service class
class TotalsSubscriptionService {
  private subscription: any = null;
  private isActive = false;
  private variables: TotalsSubscriptionVariables | null = null;
  private callbacks: TotalsSubscriptionCallbacks = {};

  async startSubscription(
    variables: TotalsSubscriptionVariables,
    callbacks: TotalsSubscriptionCallbacks = {}
  ): Promise<void> {
    try {
      // Stop existing subscription if running
      if (this.isActive) {
        await this.stopSubscription();
      }

      // Store variables and callbacks
      this.variables = variables;
      this.callbacks = { ...callbacks };

      
      const subscriptionVariables = {
        parentId: variables.parentId,
        roleId: variables.roleId,
        search: variables.search,
        from: variables.from,
        to: variables.to,
      };
      

      // Create subscription observable
      const subscriptionObservable = apolloClient.subscribe({
        query: TOTALS_UPDATED_SUBSCRIPTION,
        variables: subscriptionVariables,
      });
      
      

      // Subscribe to the observable
      this.subscription = subscriptionObservable.subscribe({
        next: (result) => {
          
          if (result.data?.totalsUpdated) {
            const updatedData = result.data.totalsUpdated as TotalsUpdatedData;
            
            
            // Call update callback
            if (this.callbacks.onUpdate) {
              this.callbacks.onUpdate(updatedData);
            }
          } else {
          }
        },
        error: (error) => {
          this.isActive = false;
          
          // Call error callback
          if (this.callbacks.onError) {
            this.callbacks.onError(error);
          }
        },
        complete: () => {
          this.isActive = false;
          
          // Call complete callback
          if (this.callbacks.onComplete) {
            this.callbacks.onComplete();
          }
        }
      });

      this.isActive = true;

      // Call connect callback
      if (this.callbacks.onConnect) {
        this.callbacks.onConnect();
      }

    } catch (error) {
      console.error('📊 Error starting totals subscription:', error);
      throw error;
    }
  }

  async stopSubscription(): Promise<void> {
    
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
    
    this.isActive = false;
    this.variables = null;
    this.callbacks = {};
    
    
  }

  async updateSubscription(
    variables: TotalsSubscriptionVariables,
    callbacks: TotalsSubscriptionCallbacks = {}
  ): Promise<void> {
    
    await this.startSubscription(variables, callbacks);
  }

  isSubscriptionActive(): boolean {
    return this.isActive;
  }

  getSubscriptionVariables(): TotalsSubscriptionVariables | null {
    return this.variables;
  }
}

// Create singleton instance
const totalsSubscriptionService = new TotalsSubscriptionService();

// Export service methods
export const startTotalsSubscription = (
  variables: TotalsSubscriptionVariables,
  callbacks: TotalsSubscriptionCallbacks = {}
) => totalsSubscriptionService.startSubscription(variables, callbacks);

export const stopTotalsSubscription = () => totalsSubscriptionService.stopSubscription();

export const updateTotalsSubscription = (
  variables: TotalsSubscriptionVariables,
  callbacks: TotalsSubscriptionCallbacks = {}
) => totalsSubscriptionService.updateSubscription(variables, callbacks);

export const isTotalsSubscriptionActive = () => totalsSubscriptionService.isSubscriptionActive();

export default totalsSubscriptionService;
